import { useCallback, useEffect, useRef, useState } from 'react';
import { api, curatedCampaignListPath } from '../api.js';
import { safeFailureMessage } from '../safe-failure.js';
import {
  COMPANY_META_SETUP_ENDPOINT,
  campaignSetupBinding,
  clearOnboardingMetaContinuation,
  companyMetaPanelAllowsContinue,
  companyMetaPanelStatus,
  consumeCompanyMetaSetupLaunch,
  existingCampaignCount,
  onboardingMetaSetupStage,
  rememberOnboardingMetaContinuation,
  setupReadinessRows,
  setupResourceChoices,
  setupSelection,
} from '../company-meta-setup.js';
import {
  checkCuratedMetaAuthorization,
  curatedMetaAuthorizationPresentation,
  curatedMetaConnectionPresentation,
  curatedMetaIdentityRows,
  metaCredentialId,
  refreshCuratedMetaConnection,
  startCuratedMetaOAuth,
  useCuratedMetaConnection,
  useSettledMetaAuthorizationState,
} from './CuratedMetaConnection.jsx';
import Beacon from './Beacon.jsx';
import { Disclosure, MetaSetupModal } from './MetaSetupModal.jsx';
import './company-meta-setup.css';

const tenant = (companyId) => ({ headers: { 'x-company-id': companyId } });
const ENDPOINT = COMPANY_META_SETUP_ENDPOINT;

function ArrowIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" /></svg>;
}

function ReadinessResults({ readiness }) {
  const rows = setupReadinessRows(readiness);
  const blockers = Array.isArray(readiness?.blockedReasons) ? readiness.blockedReasons : Array.isArray(readiness?.blockers) ? readiness.blockers : [];
  const code = readiness?.code && !['ready', 'meta_setup_required', 'meta_setup_recheck_recommended'].includes(readiness.code) ? readiness.code : '';
  if (!rows.length && !blockers.length && !code) return null;
  return <ul className="bl-msp-checks" aria-label="Meta readiness checks">
    {rows.map((row) => <li key={row.key}><span aria-label={row.ready ? 'Passed' : 'Needs attention'}>{row.ready ? '✓' : '○'}</span>{row.label}</li>)}
    {blockers.map((item) => <li key={item.code || item}><span aria-hidden="true">!</span>{safeFailureMessage({ code: item.code || item }, 'integration')}</li>)}
    {!rows.length && !blockers.length && code && <li><span aria-hidden="true">!</span>{safeFailureMessage({ code }, 'integration')}</li>}
  </ul>;
}

function assetValue(asset, fallback) {
  const label = typeof asset?.label === 'string' ? asset.label.trim() : '';
  return label ? <span title={label}>{label}</span> : <span className="bl-msp-unselected">{fallback}</span>;
}

function pageProviderId(resources, selection) {
  const page = setupResourceChoices(resources, setupSelection(selection)).pages.find((item) => item.resourceId === selection?.pageResourceId);
  return typeof page?.providerId === 'string' ? page.providerId : '';
}

// OGC-527: Company settings and campaign creation share one controller and
// the v15 modal. The Company panel foregrounds status, assets and Change
// setup; identifiers, checks and management actions stay under Details and
// Connection options with their existing confirmations and role gates.
// OGC-532: the Company panel heading claims Connected only from a successful
// connection read; failed or disabled reads say the status is unavailable.
export function companyMetaConnectionHeading({ authorizationState, authorizationPresentation = null, management = null, displayPresentation = null } = {}) {
  if (authorizationState === 'missing') return 'Connect your Meta account to get started.';
  if (authorizationState === 'unavailable') return 'Status unavailable';
  if (['reauth_required', 'disconnected'].includes(authorizationState)) return authorizationPresentation?.label || 'Status unavailable';
  if (displayPresentation?.label) return displayPresentation.label;
  if (authorizationState === 'loading') return 'Loading…';
  return management?.connectionLabel
    || (authorizationState === 'valid' ? 'Connected' : authorizationPresentation?.label || 'Status unavailable');
}

export function CompanyMetaSetup({
  companyId,
  companyName = 'Company',
  mode = 'company',
  campaignId = '',
  configurationResponse = null,
  options = null,
  draft = null,
  metaConnection,
  onAuthorize,
  onCheckAuthorization,
  onRefresh,
  onComplete,
  onStateChange,
  management = null,
  onboarding = null,
  onModalClose,
  onModalStateChange,
  campaignStep = null,
  requestApi = api,
}) {
  const surface = mode === 'campaign' ? 'campaign_meta' : 'company';
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotCompanyId, setSnapshotCompanyId] = useState('');
  const [resources, setResources] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    onModalStateChange?.(Boolean(modal));
    return () => onModalStateChange?.(false);
  }, [modal, onModalStateChange]);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const version = useRef(0);
  const validateLock = useRef(false);
  const currentCompany = useRef(companyId);
  currentCompany.current = companyId;

  const load = useCallback(async () => {
    if (!companyId) return null;
    const expected = ++version.current;
    const results = await Promise.allSettled([
      requestApi.get(ENDPOINT, tenant(companyId)),
      requestApi.get(`${ENDPOINT}/resources`, tenant(companyId)),
    ]);
    if (expected !== version.current || currentCompany.current !== companyId) return null;
    if (results[0].status === 'rejected') throw results[0].reason;
    setSnapshot(results[0].value);
    setSnapshotCompanyId(companyId);
    setStatus('ready');
    if (results[1].status === 'rejected') { setResources(null); throw results[1].reason; }
    setResources(results[1].value);
    return results[0].value;
  }, [companyId, requestApi]);

  const commitSnapshot = useCallback((next) => {
    if (currentCompany.current !== companyId) return;
    version.current += 1;
    setSnapshot(next); setSnapshotCompanyId(companyId); setStatus('ready');
  }, [companyId]);

  useEffect(() => {
    setSnapshot(null); setResources(null); setValidation(null); setError(''); setStatus('loading'); setOpening(false);
    // A browser return from Meta reopens the exact launching flow only.
    const launch = consumeCompanyMetaSetupLaunch({ companyId, surface, campaignId });
    setModal(launch ? { connectStep: launch.connectStep, confirmChange: false, campaigns: 0 } : null);
    load().catch((failure) => {
      if (currentCompany.current === companyId) { setStatus('error'); setError(safeFailureMessage(failure, 'integration')); }
    });
    return () => { version.current += 1; };
  }, [companyId, campaignId, surface, load]);

  useEffect(() => {
    if (snapshotCompanyId === companyId && snapshot) onStateChange?.({ companyId, campaignId, ...snapshot });
  }, [snapshot, snapshotCompanyId, companyId, campaignId, onStateChange]);

  // A completed background catalog sync refreshes the open modal's choices.
  const syncGeneration = metaConnection?.resources?.currentGeneration || metaConnection?.resources?.connections?.[0]?.currentGeneration || 0;
  useEffect(() => {
    if (!modal) return;
    load().catch(() => {});
  }, [Boolean(modal), syncGeneration, load]);

  const snapshotCurrent = snapshotCompanyId === companyId;
  const canManage = snapshotCurrent && status === 'ready' && snapshot?.capabilities?.canManage === true;
  const recorded = mode === 'campaign' ? campaignSetupBinding(configurationResponse, options, draft) : null;
  const active = snapshotCurrent ? snapshot?.active : null;
  const pending = snapshotCurrent ? snapshot?.pending : null;
  const binding = mode === 'company' ? active : recorded || active;
  const validationKey = mode === 'campaign'
    ? `${companyId}:${campaignId}:${configurationResponse?.configuration?.revision ?? 0}:${recorded?.id || ''}`
    : `${companyId}:${binding?.id || ''}:${binding?.revision || 0}`;
  const validationScope = useRef(validationKey);
  validationScope.current = validationKey;
  const currentValidation = validation?.key === validationKey ? validation.readiness : null;
  const authorizationPresentation = metaConnection?.status === 'ready' ? curatedMetaAuthorizationPresentation(metaConnection.payload) : null;
  // OGC-532: a failed or disabled read is unknown, never a pending "loading".
  const authorizationState = authorizationPresentation ? authorizationPresentation.state
    : ['error', 'idle'].includes(metaConnection?.status) ? 'unavailable' : 'loading';
  const connected = !['missing', 'loading', 'unavailable'].includes(authorizationState);
  // OGC-555: only display prose survives a same-identity background poll.
  // Authorization/readiness/actions continue to use the authoritative data.
  const displayPresentation = ['ready', 'loading'].includes(metaConnection?.status)
    ? (metaConnection.companyId === companyId ? metaConnection.displayOnlyPresentation : null)
      || (metaConnection.status === 'ready' ? curatedMetaConnectionPresentation(metaConnection.payload) : null)
    : null;
  const showRefreshExplanation = displayPresentation && ['refresh_required', 'refresh_in_progress', 'waiting_on_meta', 'sync_failed', 'observation_unconfirmed'].includes(displayPresentation.state);
  const canCheckStatus = typeof metaConnection?.checkStatus === 'function' && (displayPresentation?.observationExhausted || metaConnection.statusCheckInProgress || metaConnection.status === 'error');
  const settledAuthorizationState = useSettledMetaAuthorizationState(metaConnection, companyId);
  // Until the first setup read lands, say so instead of "Setup needed".
  const setupLoading = status === 'loading' && !binding && !pending;
  const panelStatus = setupLoading
    ? { state: 'loading', label: 'Loading…', tone: 'pending' }
    : mode === 'company' && authorizationState === 'unavailable' && binding
      ? { state: 'unconfirmed', label: 'Not confirmed', tone: 'pending' }
      : companyMetaPanelStatus({ binding, readiness: currentValidation || binding?.readiness, authorizationState: settledAuthorizationState, pending });
  const ready = panelStatus.state === 'ready';
  const continueAllowed = companyMetaPanelAllowsContinue(panelStatus);
  const onboardingEntry = mode === 'company' && onboarding?.active === true;

  async function validate() {
    if (validateLock.current || !canManage || !binding || (mode === 'campaign' && !campaignId)) return;
    validateLock.current = true; setValidating(true); setError('');
    const expected = validationKey;
    setValidation({ key: expected, readiness: { state: 'checking' } });
    try {
      const response = await requestApi.post(`${ENDPOINT}/validate`, {
        scope: mode === 'campaign' ? 'campaign' : 'active',
        ...(binding.id ? { setupId: binding.id, expectedRevision: binding.revision } : {}),
        ...(mode === 'campaign' ? { campaignId, configurationRevision: configurationResponse?.configuration?.revision ?? 0 } : {}),
      }, tenant(companyId));
      if (validationScope.current === expected) setValidation({ key: expected, readiness: response.readiness });
    } catch (failure) {
      if (validationScope.current === expected) {
        setValidation({ key: expected, readiness: { state: 'needs_attention', code: failure.code } });
        setError(safeFailureMessage(failure, 'integration'));
      }
    } finally { validateLock.current = false; if (currentCompany.current === companyId) setValidating(false); }
  }

  async function openSetup() {
    if (!canManage || opening) return;
    // Change setup explains, before editing, that existing campaigns keep
    // their recorded destinations; a resumed confirmed candidate skips it.
    if (!active || pending?.confirmedAt || pending?.operationId) { setModal({ connectStep: false, confirmChange: false, campaigns: 0 }); return; }
    setOpening(true);
    const expectedCompany = companyId;
    let campaigns = null;
    try { campaigns = existingCampaignCount(await requestApi.get(curatedCampaignListPath('exclude', { limit: 1 }), tenant(companyId))); }
    catch { campaigns = null; }
    setOpening(false);
    if (currentCompany.current !== expectedCompany) return;
    setModal({ connectStep: false, confirmChange: campaigns !== 0, campaigns: campaigns || 0 });
  }

  async function completed(next, { advance = true } = {}) {
    commitSnapshot(next); setModal(null);
    await onComplete?.(next, { entry: onboardingEntry ? 'onboarding' : mode, advance });
  }

  const setupLabel = authorizationState === 'missing' && !binding ? 'Connect Meta'
    : active ? 'Change setup' : pending ? 'Continue setup' : 'Set up Meta';
  const setupButton = (secondary) => canManage && <button type="button" className={`bl-ms-button ${secondary ? 'is-secondary' : 'is-primary'}`} aria-disabled={opening ? 'true' : undefined} aria-busy={opening ? 'true' : undefined} onClick={openSetup}>{opening ? <><Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /> Opening…</> : setupLabel}</button>;
  const statusTag = <span className={`bl-msp-status ${ready ? 'is-ready' : 'is-pending'}`} data-state={panelStatus.state}><span aria-hidden="true">{ready ? '✓' : ['checking', 'authorization_checking', 'loading'].includes(panelStatus.state) ? '…' : '○'}</span><span>{panelStatus.label}</span></span>;
  const destination = binding?.destination || pending?.destination || {};
  const assets = <dl className="bl-msp-assets">
    <div><dt>Facebook Page</dt><dd>{assetValue(destination.page, binding || pending ? 'Saved Page · access needs verification' : setupLoading ? 'Loading…' : 'Not selected')}</dd></div>
    <div><dt>Ad account</dt><dd>{assetValue(destination.adAccount, binding || pending ? 'Saved ad account' : setupLoading ? 'Loading…' : 'Not selected')}</dd></div>
    <div><dt>Instant Form</dt><dd>{binding ? assetValue(binding.destination?.instantForm, 'Saved Instant Form') : <span className="bl-msp-unselected">{setupLoading ? 'Loading…' : 'Review during setup'}</span>}</dd></div>
  </dl>;
  const note = setupLoading ? '' : ready ? 'Lead access and form verified.'
    : panelStatus.state === 'stale' ? 'Check the connection to refresh verification.'
      : ['checking', 'authorization_checking'].includes(panelStatus.state) ? 'Checking Meta…'
        : panelStatus.state === 'unconfirmed' ? 'Meta connection status could not be loaded, so readiness cannot be confirmed right now.'
        : panelStatus.state === 'renewal_required' ? (mode === 'campaign' ? 'Renew Meta access before continuing this campaign.' : 'Renew Meta access to keep this setup working.')
        : panelStatus.state === 'recheck' ? (mode === 'campaign' ? 'Meta could not confirm access just now. You can continue; access is checked again before launch.' : 'Meta could not confirm access just now. Use the check under Connection options to refresh it.')
        : panelStatus.state === 'needs_attention' ? 'Check the connection to see what needs attention.'
          : mode === 'campaign' ? 'Finish setup, then continue to Targeting.' : 'Complete setup to start collecting leads.';
  const loading = status === 'loading' && <p className="bl-msp-readonly"><Beacon size="control" label="Loading company setup…" delayMs={0} slowAfterMs={null} /></p>;
  const readOnly = status === 'ready' && !canManage && <p className="bl-msp-readonly">A company owner or admin manages Meta setup and validation.</p>;
  const failure = error && <p className="bl-msp-error" role="alert">{error}{status === 'error' && <> <button type="button" className="bl-ms-text-button" onClick={() => load().then(() => setError('')).catch((reason) => setError(safeFailureMessage(reason, 'integration')))}>Try again</button></>}</p>;

  const modalElement = modal && snapshotCurrent && snapshot && <MetaSetupModal
    key={companyId}
    entry={onboardingEntry ? 'onboarding' : mode}
    companyId={companyId}
    companyName={companyName}
    initialSnapshot={snapshot}
    resources={resources}
    canManage={canManage}
    metaConnection={metaConnection}
    onAuthorize={onAuthorize}
    onCheckAuthorization={onCheckAuthorization}
    onRefresh={onRefresh}
    onReload={load}
    surface={surface}
    campaignId={campaignId}
    requestApi={requestApi}
    onSnapshot={commitSnapshot}
    onClose={() => { setModal(null); onboarding?.onClose?.(); onModalClose?.(); }}
    onComplete={completed}
    connectStep={modal.connectStep}
    confirmChange={modal.confirmChange}
    existingCampaigns={modal.campaigns}
  />;

  if (mode === 'campaign') {
    return <>
      <section className="bl-msp bl-msp-card" aria-label="Meta campaign resources">
        <header className="bl-msp-header"><div><span className="bl-kicker">Campaign destination</span><h3>Meta campaign resources</h3></div>{statusTag}</header>
        {assets}
        <footer className="bl-msp-footer">
          {note && <span className="bl-msp-inline-note">{note}</span>}
          <span className="bl-msp-footer-actions">
            {canManage && recorded && campaignId && <button type="button" className="bl-ms-text-button" disabled={validating} aria-busy={validating ? 'true' : undefined} onClick={validate}>{validating ? <><Beacon size="inline" className="is-current" delayMs={0} /> Checking…</> : 'Check connection'}</button>}
            {setupButton(ready)}
          </span>
        </footer>
        {loading}{readOnly}{failure}
        {currentValidation && <div className="bl-msp-readonly"><ReadinessResults readiness={currentValidation} /></div>}
      </section>
      <footer className="bl-msp bl-msp-step-footer">
        <span className="bl-msp-next-hint">Your campaign stays a draft.</span>
        {continueAllowed && campaignStep?.onContinue && <button type="button" className="bl-ms-button is-primary" disabled={campaignStep.busy} onClick={campaignStep.onContinue}>Continue to targeting <ArrowIcon /></button>}
      </footer>
      {modalElement}
    </>;
  }

  const identity = curatedMetaIdentityRows(metaConnection?.connection).find((row) => row.label === 'Meta profile');
  const providerPageId = pageProviderId(resources, binding?.selection);
  const rows = management?.rows || [];
  return <>
    <div className="bl-msp bl-msp-card">
      <header className="bl-msp-header">
        <div className="bl-msp-heading"><div><h3>Meta Ads</h3><p>{companyMetaConnectionHeading({ authorizationState, authorizationPresentation, management, displayPresentation })}</p></div></div>
        {statusTag}
      </header>
      {(showRefreshExplanation || canCheckStatus) && <div className="bl-msp-readonly bl-msp-refresh-explanation" role="status"><p>{showRefreshExplanation ? displayPresentation.description : metaConnection.statusCheckInProgress ? 'Checking the latest Meta status…' : 'Meta connection status could not be confirmed. Check status to try again.'}</p>{canCheckStatus && <button type="button" className="bl-ms-text-button" disabled={metaConnection.status === 'loading'} onClick={metaConnection.checkStatus}>{metaConnection.statusCheckInProgress ? 'Checking status…' : 'Check status'}</button>}</div>}
      {assets}
      {!setupLoading && <footer className="bl-msp-footer">{note && <span className="bl-msp-inline-note">{note}</span>}{setupButton(false)}</footer>}
      {loading}{readOnly}{failure}
      <div className="bl-msp-details">
        <Disclosure className="bl-msp-disclosure" summary={<><span>Details</span><span className="bl-msp-disclosure-hint">Access, form links &amp; checks</span><span className="bl-msp-chevron" aria-hidden="true">⌄</span></>}>
          <div className="bl-msp-disclosure-body">
            <dl className="bl-msp-detail-list">
              <div><dt>Meta profile</dt><dd>{identity?.value || (connected ? 'Connected Meta profile' : 'Not connected')}</dd></div>
              <div><dt>Facebook Page ID</dt><dd>{providerPageId || (binding ? 'Saved Page' : 'Not selected')}</dd></div>
              <div><dt>Privacy policy</dt><dd>{binding?.privacyPolicyUrl || snapshot?.profile?.privacyPolicyUrl || 'Not set'}</dd></div>
              <div><dt>Website after submission</dt><dd>{binding?.followUpUrl || snapshot?.profile?.followUpUrl || snapshot?.profile?.websiteUrl || 'Not set'}</dd></div>
            </dl>
            <div className="bl-msp-validation-row">
              <div className="bl-msp-row-copy">
                <strong>{ready ? 'All checks passed'
                  : panelStatus.state === 'renewal_required' ? 'Meta access needs renewal'
                    : panelStatus.state === 'recheck' ? 'Meta access not confirmed'
                      : binding ? (['checking', 'authorization_checking'].includes(panelStatus.state) ? 'Checking…' : 'Checks need attention') : 'Checks run after setup'}</strong>
                <p>{ready ? 'Page access, lead permissions, and Instant Form are ready.'
                  : panelStatus.state === 'renewal_required' ? 'Renew Meta access to keep Page access, lead permissions and Instant Form working.'
                    : panelStatus.state === 'recheck' ? 'Saved checks passed, but Meta could not confirm access just now.'
                      : binding ? 'Check the connection to refresh Page access, lead permissions and Instant Form.' : 'Your Page, permissions, and form will be checked before finishing.'}</p>
                <ReadinessResults readiness={currentValidation || binding?.readiness} />
              </div>
              {canManage && <button type="button" className="bl-ms-button is-secondary" disabled={!binding || validating} aria-busy={validating ? 'true' : undefined} onClick={validate}>{validating ? <><Beacon size="inline" className="is-current" delayMs={0} /> Checking…</> : 'Check connection'}</button>}
            </div>
            <p className="bl-msp-details-hint">{snapshot?.formUpdateAvailable ? 'Saved company URLs differ from the current form. Use Change setup to review an updated form.' : 'Use Change setup to update the form URLs.'}</p>
          </div>
        </Disclosure>
        {management?.canManage && <Disclosure className="bl-msp-disclosure" summary={<><span>Connection options</span><span className="bl-msp-disclosure-hint">Manage access</span><span className="bl-msp-chevron" aria-hidden="true">⌄</span></>}>
          <div className="bl-msp-disclosure-body">
            {rows.map((row) => <div className="bl-msp-admin-row" key={row.key}>
              <div className="bl-msp-row-copy"><strong>{row.title}</strong><p>{row.description}</p></div>
              <button type="button" className={row.danger ? 'bl-ms-text-button bl-msp-danger' : 'bl-ms-button is-secondary'} disabled={row.disabled} aria-busy={row.busy ? 'true' : undefined} aria-describedby={row.describedBy} onClick={row.onClick}>
                {row.busy ? <><Beacon size="inline" className="is-current" delayMs={0} /> {row.busyLabel}</> : row.action}
              </button>
            </div>)}
            {!rows.length && <p className="bl-msp-details-hint">No connection actions are available right now.</p>}
          </div>
        </Disclosure>}
      </div>
    </div>
    {modalElement}
  </>;
}

// OGC-527: self-service onboarding opens the same modal on the welcome screen.
// OAuth uses the allowlisted Company return surface; the user/company-scoped
// continuation only decides where verified completion goes afterwards.
export function OnboardingMetaSetup({ companyId, companyName, userId, beforeAuthorize, onReady, onFailure, onClose, onComplete, requestApi = api }) {
  const metaConnection = useCuratedMetaConnection({ companyId });
  const [snapshot, setSnapshot] = useState(null);
  const [resources, setResources] = useState(null);
  const latest = useRef(null);
  const announced = useRef(false);
  const version = useRef(0);

  const load = useCallback(async () => {
    const expected = ++version.current;
    const [setup, inventory] = await Promise.allSettled([
      requestApi.get(ENDPOINT, tenant(companyId)),
      requestApi.get(`${ENDPOINT}/resources`, tenant(companyId)),
    ]);
    // Only the newest read may replace state.
    if (expected !== version.current) return latest.current;
    if (setup.status === 'rejected') throw setup.reason;
    latest.current = setup.value; setSnapshot(setup.value);
    setResources(inventory.status === 'fulfilled' ? inventory.value : null);
    return setup.value;
  }, [companyId, requestApi]);

  useEffect(() => {
    load().then(() => { if (!announced.current) { announced.current = true; onReady?.(); } }, (failure) => onFailure?.(failure));
  }, [load]);

  const syncGeneration = metaConnection?.resources?.currentGeneration || metaConnection?.resources?.connections?.[0]?.currentGeneration || 0;
  useEffect(() => { if (snapshot) load().catch(() => {}); }, [syncGeneration]);

  if (!snapshot) return null;
  const credentialId = metaCredentialId(metaConnection.connection);
  async function authorize() {
    await beforeAuthorize?.();
    rememberOnboardingMetaContinuation({ userId, companyId });
    let launch = null;
    try { launch = await startCuratedMetaOAuth({ companyId, returnSurface: 'company', credentialId }); }
    finally { if (!launch?.started) clearOnboardingMetaContinuation(); }
  }
  return <MetaSetupModal
    entry="onboarding"
    companyId={companyId}
    companyName={companyName}
    initialSnapshot={snapshot}
    resources={resources}
    canManage={snapshot.capabilities?.canManage === true}
    metaConnection={metaConnection}
    onAuthorize={authorize}
    onCheckAuthorization={credentialId ? async () => { await checkCuratedMetaAuthorization({ companyId, credentialId }); metaConnection.reload(); } : undefined}
    onRefresh={credentialId && metaConnection.capabilities?.canRefresh === true ? async () => { await refreshCuratedMetaConnection({ companyId, credentialId }); metaConnection.reload(); } : undefined}
    onReload={load}
    surface="company"
    requestApi={requestApi}
    onSnapshot={(next) => { latest.current = next; }}
    onClose={() => onClose?.(onboardingMetaSetupStage(latest.current))}
    onComplete={(next, options) => { clearOnboardingMetaContinuation(); onComplete?.(next, options); }}
  />;
}
